import {
  Archive,
  Eye,
  EyeOff,
  FileCheck,
  Pause,
  Play,
  Send,
  Square,
  Undo2,
} from "lucide-react";

import { ListingStatus, type Prisma } from "@/.zenstack/logical-prisma-client";
import type {
  // ListingEditorType,
  ValidationSeverity,
} from "@/components/listing-editor/listing-editor-steps";
import { type Session } from "@/lib/auth/better-auth";
import { isPlatformAdmin } from "@/lib/auth/types";
import { validateListingForTransition } from "@/lib/validation/listing-validation";

/**
 * Define the select structure for state machine operations.
 * This specifies exactly which fields are needed for state transitions and validations.
 */
export const LISTING_STATE_MACHINE_SELECT = {
  id: true,
  createdById: true,
  updatedById: true,
  businessId: true,
  status: true,
  business: {
    select: {
      organizationId: true,
      organization: {
        select: {
          members: {
            select: {
              userId: true,
              role: true,
            },
          },
        },
      },
    },
  },
  atAuction: {
    select: {
      auctionStartDate: true,
      auctionEndDate: true,
      reserveBidAmount: true,
      bids: {
        select: {
          id: true,
          currentBidAmount: true,
        },
      },
    },
  },
  forLease: {
    select: {
      availableFrom: true,
      leaseTermMonths: true,
    },
  },
  files: {
    select: {
      id: true,
      url: true,
      name: true,
      type: true,
      status: true,
    },
  },
} as const;

/**
 * Type-safe payload for state machine operations.
 * Uses Prisma's ListingGetPayload pattern for compile-time type safety.
 *
 * Note: files field is made optional since not all callers have access to files data.
 * The validation functions handle the absence of files by using `listing.files || []`.
 */
export type ListingForStateMachine = Omit<
  Prisma.ListingGetPayload<{
    select: typeof LISTING_STATE_MACHINE_SELECT;
  }>,
  "files"
> & {
  files?: Prisma.ListingGetPayload<{
    select: typeof LISTING_STATE_MACHINE_SELECT;
  }>["files"];
};

export type ListingAction =
  | "SUBMIT"
  | "APPROVE"
  | "ACTIVATE"
  | "DISAPPROVE"
  | "WITHDRAW"
  | "PAUSE"
  | "RESUME"
  | "STOP"
  | "CLOSE"
  | "ARCHIVE"
  | "HIDE"
  | "UNHIDE";

export class ListingActionClass {
  constructor(
    public readonly type: ListingAction,
    public readonly label: string,
    public readonly icon: React.ComponentType<{ className?: string }>,
    public readonly description?: string,
    public readonly requiresConfirmationFlag?: boolean,
    public readonly isDestructiveFlag?: boolean,
  ) {}

  getLabel(): string {
    return this.label;
  }

  getIcon(): React.ComponentType<{ className?: string }> {
    return this.icon;
  }

  getDescription(): string | undefined {
    return this.description;
  }

  requiresConfirmation(): boolean {
    return this.requiresConfirmationFlag ?? false;
  }

  isDestructive(): boolean {
    return this.isDestructiveFlag ?? false;
  }

  /**
   * Checks if a user has permission to perform this action
   * @param userId The user ID to check permissions for
   * @param listing The listing to check permissions for
   * @returns true if the user has permission to perform this action
   */
  isPermissibleFor(
    userId: string,
    listing: { createdById: string; updatedById: string | null },
  ): boolean {
    // Allowed if the user created or last edited the listing
    if (userId === listing.createdById || userId === listing.updatedById) {
      return true;
    }

    // TODO: Check if user is a member of the business organization with owner or admin role
    // This requires loading organization membership data separately
    // For now, we rely on creator/updater checks above

    return false;
  }
}

export class ListingActionRegistry {
  private static readonly actions: Record<ListingAction, ListingActionClass> = {
    SUBMIT: new ListingActionClass(
      "SUBMIT",
      "Submit for Approval",
      Send,
      "Submit listing for admin approval",
      true,
    ),
    APPROVE: new ListingActionClass(
      "APPROVE",
      "Approve",
      FileCheck,
      "Approve listing for activation",
      true,
    ),
    ACTIVATE: new ListingActionClass(
      "ACTIVATE",
      "Activate",
      Play,
      "Activate listing for bidding",
      true,
    ),
    DISAPPROVE: new ListingActionClass(
      "DISAPPROVE",
      "Disapprove",
      Undo2,
      "Disapprove listing and return to draft",
      true,
      true,
    ),
    WITHDRAW: new ListingActionClass(
      "WITHDRAW",
      "Withdraw Submission",
      Undo2,
      "Withdraw listing from review and return to draft",
      true,
    ),
    PAUSE: new ListingActionClass(
      "PAUSE",
      "Pause Listing",
      Pause,
      "Pause active listing",
      true,
    ),
    RESUME: new ListingActionClass(
      "RESUME",
      "Resume Listing",
      Play,
      "Resume paused listing",
      false,
    ),
    STOP: new ListingActionClass(
      "STOP",
      "Take Down",
      Square,
      "Stop listing and take it down",
      true,
      true,
    ),
    CLOSE: new ListingActionClass(
      "CLOSE",
      "Close Listing",
      Square,
      "Close listing after auction ends",
      false,
    ),
    ARCHIVE: new ListingActionClass(
      "ARCHIVE",
      "Archive",
      Archive,
      "Archive completed listing",
      true,
    ),
    HIDE: new ListingActionClass(
      "HIDE",
      "Hide",
      EyeOff,
      "Hide listing from dashboard",
      true,
    ),
    UNHIDE: new ListingActionClass(
      "UNHIDE",
      "Unhide",
      Eye,
      "Restore listing to dashboard",
      false,
    ),
  };

  static getAction(actionType: ListingAction): ListingActionClass {
    return this.actions[actionType];
  }

  static getAllActions(): ListingActionClass[] {
    return Object.values(this.actions);
  }

  static getActionsByStatus(
    status: ListingStatus,
    role: "admin" | "landlord",
  ): ListingActionClass[] {
    const stateMachine = new ListingStateMachine();
    const actionTypes = stateMachine.getAvailableActions(status, role);
    return actionTypes.map((actionType) => this.getAction(actionType));
  }

  static getActionLabel(actionType: ListingAction): string {
    return this.getAction(actionType).getLabel();
  }

  static getActionIcon(
    actionType: ListingAction,
  ): React.ComponentType<{ className?: string }> {
    return this.getAction(actionType).getIcon();
  }
}

// Individual validation result
export interface ValidationResult {
  isValid: boolean;
  message: string;
  severity: ValidationSeverity;
}

// Multiple validations per transition
interface StateTransition {
  from: ListingStatus;
  to: ListingStatus;
  action: ListingAction;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description: string;
  validations: Array<{
    validate: (listing: ListingForStateMachine) => boolean;
    message: string;
    severity: ValidationSeverity;
  }>;
}

export class ListingStateMachine {
  private transitions: StateTransition[] = [
    // Draft -> Pending (submit for approval)
    {
      from: ListingStatus.DRAFT,
      to: ListingStatus.PENDING,
      action: "SUBMIT",
      icon: Send,
      label: "Submit for Review",
      description: "Submit listing for admin review and approval",
      validations: [
        {
          validate: (listing) => {
            // Use the same validation logic as SummaryPage
            // Note: Casts to 'any' are intentional - we're passing subsets of fields
            // that the validation function needs, not the full types
            const result = validateListingForTransition(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              listing as any,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (listing.files || []) as any,
            );
            return result.isValid;
          },
          message: "Listing must pass all validation checks before submission",
          severity: "error" as ValidationSeverity,
        },
        // FEATURE: add warning/recommendation schema.
      ],
    },

    // Pending -> Approved (admin approval, future auction)
    {
      from: ListingStatus.PENDING,
      to: ListingStatus.APPROVED,
      action: "APPROVE",
      icon: FileCheck,
      label: "Approve Listing",
      description: "Approve listing for activation",
      validations: [
        {
          validate: (listing) => {
            // Use the same validation logic as SummaryPage
            // Note: Casts to 'any' are intentional - we're passing subsets of fields
            // that the validation function needs, not the full types
            const result = validateListingForTransition(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              listing as any,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (listing.files || []) as any,
            );

            return result.isValid;
          },
          message:
            "Listing must pass all validation checks and auction dates must not have started",
          severity: "error" as ValidationSeverity,
        },
        // FEATURE: add warning/recommendation schema.
      ],
    },

    // Pending -> Draft (admin disapproval)
    {
      from: ListingStatus.PENDING,
      to: ListingStatus.DRAFT,
      action: "DISAPPROVE",
      icon: Undo2,
      label: "Send Back to Draft",
      description: "Return listing to draft for landlord to fix issues",
      validations: [
        {
          validate: () => true,
          message: "",
          severity: "error" as ValidationSeverity,
        },
      ],
    },

    // Pending -> Draft (landlord withdrawal)
    {
      from: ListingStatus.PENDING,
      to: ListingStatus.DRAFT,
      action: "WITHDRAW",
      icon: Undo2,
      label: "Withdraw Submission",
      description: "Withdraw your listing from review and return to draft",
      validations: [
        {
          validate: () => true,
          message: "",
          severity: "error" as ValidationSeverity,
        },
      ],
    },

    // Active -> Paused
    {
      from: ListingStatus.ACTIVE,
      to: ListingStatus.PAUSED,
      action: "PAUSE",
      icon: Pause,
      label: "Pause Auction",
      description: "Temporarily pause the auction",
      validations: [
        {
          validate: (listing) => {
            // Check if there are any bids
            const hasBids =
              listing.atAuction?.bids && listing.atAuction.bids.length > 0;
            return !hasBids; // Validation passes if there are NO bids
          },
          message:
            "Warning: Pausing an auction with active bids may disappoint bidders. Consider the impact before proceeding.",
          severity: "warning" as ValidationSeverity,
        },
        {
          validate: (listing) => {
            // Check if any bid meets or exceeds the reserve price
            const reservePrice = listing.atAuction?.reserveBidAmount;
            const bids = listing.atAuction?.bids || [];

            if (!reservePrice || bids.length === 0) return true;

            const hasReserveMet = bids.some(
              (bid) => Number(bid.currentBidAmount) >= Number(reservePrice),
            );
            return !hasReserveMet; // Validation passes if reserve is NOT met
          },
          message:
            "IMPORTANT: At least one bid has met the reserve price. Pausing this auction may create a legal obligation to sell. Consult legal advice before proceeding.",
          severity: "error" as ValidationSeverity,
        },
      ],
    },

    // Paused -> Active
    {
      from: ListingStatus.PAUSED,
      to: ListingStatus.ACTIVE,
      action: "RESUME",
      icon: Play,
      label: "Resume Auction",
      description: "Resume the paused auction",
      validations: [
        {
          validate: () => true,
          message: "",
          severity: "error" as ValidationSeverity,
        },
      ],
    },

    // Active -> Stopped
    {
      from: ListingStatus.ACTIVE,
      to: ListingStatus.STOPPED,
      action: "STOP",
      icon: Square,
      label: "Stop Auction",
      description: "Stop the auction immediately",
      validations: [
        {
          validate: (listing) => {
            // Check if there are any bids
            const hasBids =
              listing.atAuction?.bids && listing.atAuction.bids.length > 0;
            return !hasBids; // Validation passes if there are NO bids
          },
          message:
            "Warning: Stopping an auction with active bids may expose you to legal liability. Bidders have invested time and effort in this auction.",
          severity: "warning" as ValidationSeverity,
        },
        {
          validate: (listing) => {
            // Check if any bid meets or exceeds the reserve price
            const reservePrice = listing.atAuction?.reserveBidAmount;
            const bids = listing.atAuction?.bids || [];

            if (!reservePrice || bids.length === 0) return true;

            const hasReserveMet = bids.some(
              (bid) => Number(bid.currentBidAmount) >= Number(reservePrice),
            );
            return !hasReserveMet; // Validation passes if reserve is NOT met
          },
          message:
            "CRITICAL: At least one bid has met the reserve price, creating a binding obligation to sell. Stopping this auction will likely result in legal action. DO NOT proceed without legal counsel.",
          severity: "error" as ValidationSeverity,
        },
      ],
    },

    // Active -> Closed (automatic)
    {
      from: ListingStatus.ACTIVE,
      to: ListingStatus.CLOSED,
      action: "CLOSE",
      icon: EyeOff,
      label: "Close Auction",
      description: "Close the auction after end date",
      validations: [
        {
          validate: (listing) => {
            const now = new Date();
            const end = listing.atAuction?.auctionEndDate
              ? new Date(listing.atAuction.auctionEndDate)
              : null;
            return !!end && end <= now;
          },
          message: "Auction end date must be in the past to close",
          severity: "error" as ValidationSeverity,
        },
      ],
    },

    // Closed -> Archived (landlord or admin)
    {
      from: ListingStatus.CLOSED,
      to: ListingStatus.ARCHIVED,
      action: "ARCHIVE",
      icon: Archive,
      label: "Archive Listing",
      description: "Archive the closed listing",
      validations: [
        {
          validate: (listing) => {
            const now = new Date();
            const end = listing.atAuction?.auctionEndDate
              ? new Date(listing.atAuction.auctionEndDate)
              : null;
            if (!end) return false;
            const minArchiveTime = new Date(
              end.getTime() + 7 * 24 * 60 * 60 * 1000,
            ); // 1 week after end
            return now >= minArchiveTime;
          },
          message: "Listing can be archived 1 week after auction end date",
          severity: "error" as ValidationSeverity,
        },
      ],
    },

    // Stopped -> Archived
    {
      from: ListingStatus.STOPPED,
      to: ListingStatus.ARCHIVED,
      action: "ARCHIVE",
      icon: Archive,
      label: "Archive Listing",
      description: "Archive the stopped listing",
      validations: [
        {
          validate: () => true,
          message: "",
          severity: "error" as ValidationSeverity,
        },
      ],
    },

    // Draft -> Hidden
    {
      from: ListingStatus.DRAFT,
      to: ListingStatus.HIDDEN,
      action: "HIDE",
      icon: EyeOff,
      label: "Hide Listing",
      description: "Hide draft listing from dashboard",
      validations: [
        {
          validate: () => true,
          message: "",
          severity: "error" as ValidationSeverity,
        },
      ],
    },

    // Closed -> Hidden
    {
      from: ListingStatus.CLOSED,
      to: ListingStatus.HIDDEN,
      action: "HIDE",
      icon: EyeOff,
      label: "Hide Listing",
      description: "Hide closed listing from dashboard",
      validations: [
        {
          validate: () => true,
          message: "",
          severity: "error" as ValidationSeverity,
        },
      ],
    },

    // Hidden -> Draft (if auction dates would have been active)
    {
      from: ListingStatus.HIDDEN,
      to: ListingStatus.DRAFT,
      action: "UNHIDE",
      icon: Eye,
      label: "Unhide Listing",
      description: "Restore listing to dashboard",
      validations: [
        {
          validate: (listing) => {
            const now = new Date();
            const start = listing.atAuction?.auctionStartDate
              ? new Date(listing.atAuction.auctionStartDate)
              : null;
            const end = listing.atAuction?.auctionEndDate
              ? new Date(listing.atAuction.auctionEndDate)
              : null;
            return !!start && !!end && start > now && end > now;
          },
          message:
            "Cannot unhide listing - auction dates must be in the future to return to draft state",
          severity: "error" as ValidationSeverity,
        },
      ],
    },

    // Hidden -> Closed (if auction dates would have been past)
    {
      from: ListingStatus.HIDDEN,
      to: ListingStatus.CLOSED,
      action: "CLOSE",
      icon: Eye,
      label: "Unhide Listing",
      description: "Restore listing to dashboard",
      validations: [
        {
          validate: (listing) => {
            const now = new Date();
            const end = listing.atAuction?.auctionEndDate
              ? new Date(listing.atAuction.auctionEndDate)
              : null;
            return !!end && end <= now;
          },
          message:
            "Cannot unhide listing - auction dates are past, listing will be closed",
          severity: "error" as ValidationSeverity,
        },
      ],
    },
  ];

  // Check if a state transition is valid
  canTransition(
    from: ListingStatus,
    action: ListingAction,
    listing: ListingForStateMachine,
  ): { valid: boolean; errors: ValidationResult[] } {
    const transition = this.transitions.find(
      (t) => t.from === from && t.action === action,
    );

    if (!transition) {
      return {
        valid: false,
        errors: [
          {
            isValid: false,
            message: `Invalid transition: ${action} not allowed from ${from}`,
            severity: "error" as ValidationSeverity,
          },
        ],
      };
    }

    const validationResults: ValidationResult[] = [];
    let allValid = true;

    for (const validation of transition.validations) {
      const isValid = validation.validate(listing);
      if (!isValid) {
        allValid = false;
      }
      validationResults.push({
        isValid,
        message: validation.message,
        severity: validation.severity,
      });
    }

    return {
      valid: allValid,
      errors: validationResults.filter((result) => !result.isValid),
    };
  }

  // Get the next state for a given action
  getNextState(
    currentState: ListingStatus,
    action: ListingAction,
  ): ListingStatus {
    const transition = this.transitions.find(
      (t) => t.from === currentState && t.action === action,
    );
    return transition ? transition.to : currentState;
  }

  // Get available actions for a state
  // TODO: Move this to the state transition object to consolidate all state logic.
  // this could be done as a getAvailableActions(Session) to encapsulate the check on
  // admin / landlord, and for employees' permissions.
  getAvailableActions(
    state: ListingStatus,
    role: "admin" | "landlord",
  ): ListingAction[] {
    return this.transitions
      .filter((t) => t.from === state)
      .filter((t) => {
        if (role === "landlord") {
          // Admin-only actions that landlords cannot perform
          const adminOnlyActions = [
            "APPROVE",
            "DISAPPROVE",
            "PAUSE",
            "RESUME",
            "STOP",
            "CLOSE",
          ];

          if (adminOnlyActions.includes(t.action)) {
            return false;
          }

          // Landlords can HIDE only from DRAFT status (soft-delete drafts)
          if (t.action === "HIDE" && t.from !== ListingStatus.DRAFT) {
            return false;
          }

          // Landlords can UNHIDE only from HIDDEN status
          if (t.action === "UNHIDE" && t.from !== ListingStatus.HIDDEN) {
            return false;
          }

          return true;
        }
        return true;
      })
      .map((t) => t.action);
  }

  // Get available actions with their info for a state
  getAvailableActionsWithInfo(
    state: ListingStatus,
    role: "admin" | "landlord",
  ): ListingActionClass[] {
    const actions = this.getAvailableActions(state, role);
    return actions.map((actionType) =>
      ListingActionRegistry.getAction(actionType),
    );
  }

  // Permission checks
  canLandlordEdit(listing: { status: ListingStatus }): boolean {
    return listing.status === ListingStatus.DRAFT; // landlords are locked out once the submit to pending
  }

  canAdminEdit(listing: { status: ListingStatus }): boolean {
    return (
      listing.status === ListingStatus.PENDING ||
      listing.status === ListingStatus.DRAFT
    );
  }

  canBid(
    listing: {
      status: ListingStatus;
      business?: {
        organizationId: string;
        organization?: {
          members: Array<{
            userId: string;
            role: string;
          }>;
        };
      } | null;
    },
    userId?: string | null,
  ): boolean {
    // Must be an active listing to bid
    if (listing.status !== ListingStatus.ACTIVE) return false;

    // If no userId provided, allow (will be caught by auth guards)
    if (!userId) return true;

    // Check if user is a member of the business organization
    // Business members CANNOT bid on their own listings
    const businessMembers = listing.business?.organization?.members ?? [];
    const isMember = businessMembers.some((member) => member.userId === userId);

    // Return true only if user is NOT a member
    return !isMember;
  }

  canComment(listing: { status: ListingStatus }): boolean {
    return listing.status === ListingStatus.ACTIVE;
  }

  canPreview(
    listing:
      | {
          status: ListingStatus;
          createdById: string;
          updatedById: string | null;
          businessId: string;
          business?: {
            organizationId: string;
            organization?: {
              members: Array<{
                userId: string;
                role: string;
              }>;
            };
          } | null;
        }
      | null
      | undefined,
    session?: Session | null,
  ): boolean {
    // Guard against null/undefined listing
    if (!listing) return false;

    const state = listing.status;

    // Guard against null status
    if (!state) return false;

    // First check if listing is in a previewable state,
    // anything prior to going active.
    const isPreviewableState =
      state === ListingStatus.DRAFT ||
      state === ListingStatus.PENDING ||
      state === ListingStatus.APPROVED ||
      state === ListingStatus.HIDDEN;
    if (!isPreviewableState) return false;

    // If no session provided, don't allow previewing
    if (!session?.user) return false;

    // Check authorization - only specific users can preview draft/pending listings

    // 1. Check if user is a platform admin (via User.role field from admin plugin)
    const isPlatformAdminFlag = isPlatformAdmin(session?.user);
    if (isPlatformAdminFlag) return true;

    // 2. Check if user is the creator of the listing
    if (session.user.id === listing.createdById) return true;

    // 3. Check if user is the last updater of the listing
    if (session.user.id === listing.updatedById) return true;

    // 4. Check if user is a member of the business organization with owner or admin role
    const businessMembers = listing.business?.organization?.members ?? [];
    const userMembership = businessMembers.find(
      (member) => member.userId === session.user.id,
    );
    if (userMembership) {
      const hasAdminRole =
        userMembership.role === "owner" || userMembership.role === "admin";
      if (hasAdminRole) return true;
    }

    return false;
  }
}
